import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Download, Share2, Copy, Check, Link as LinkIcon, Send } from "lucide-react";

// Plyx Promo / Advert Material page.
//
// Two modes, distinguished by route:
//   • `/plyx/promo`            → site-wide assets (Plyx wordmark + QR)
//   • `/plyx/v/:videoId/promo` → per-video assets (frame + QR to that clip)
//
// Every preset is rendered server-side as a PNG by `/api/plyx/promo/poster`
// so the user just taps Download and gets a real, shareable file. We
// avoid client-side html-to-image so the output is identical regardless
// of browser, OS, or screen DPR.

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const PRESETS = [
  { id: "square", label: "WhatsApp / IG Square", dim: "1080×1080", aspect: "1/1" },
  { id: "story",  label: "Story / TikTok",       dim: "1080×1920", aspect: "9/16" },
  { id: "wide",   label: "Twitter / LinkedIn",   dim: "1200×675",  aspect: "16/9" },
  { id: "flyer",  label: "Printable Flyer",      dim: "1080×1620", aspect: "2/3" },
];

const SUGGESTED_CAPTIONS = [
  "Plyx — where African moments become movements. Join the creators changing the feed → ",
  "I'm on Plyx. Vibes, talent, and tiny revolutions, one video at a time. Watch → ",
  "Found my new favourite app. Plyx is what TikTok wishes it was for African creators → ",
];

export default function PlyxPromo() {
  const { videoId } = useParams();
  const [params] = useSearchParams();
  // Allow ?video_id=... as an alternative to the route param so the
  // page works both as `/plyx/v/:id/promo` and `/plyx/promo?video_id=...`.
  const vid = videoId || params.get("video_id") || null;

  const [meta, setMeta] = useState(null);
  const [copied, setCopied] = useState(null);
  const [captionIdx, setCaptionIdx] = useState(0);

  useEffect(() => {
    const url = vid
      ? `${BACKEND}/api/plyx/promo/meta?video_id=${encodeURIComponent(vid)}`
      : `${BACKEND}/api/plyx/promo/meta`;
    fetch(url).then((r) => r.json()).then(setMeta).catch(() => {});
  }, [vid]);

  const shareUrl = meta?.share_url || (
    typeof window !== "undefined"
      ? `${window.location.origin}${vid ? `/plyx/v/${vid}` : "/"}`
      : ""
  );
  const caption = useMemo(
    () => SUGGESTED_CAPTIONS[captionIdx] + shareUrl,
    [captionIdx, shareUrl],
  );

  const posterUrl = (preset) => {
    const u = new URL(`${BACKEND}/api/plyx/promo/poster`);
    u.searchParams.set("preset", preset);
    if (vid) u.searchParams.set("video_id", vid);
    return u.toString();
  };

  const handleDownload = async (preset) => {
    try {
      const res = await fetch(posterUrl(preset));
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `plyx-${vid ? vid + "-" : ""}${preset}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      console.error("[plyx promo] download failed", e);
    }
  };

  // ── One-tap "Send to WhatsApp with poster attached" ───────────────
  // Uses the Web Share API Level 2 (`navigator.canShare({ files })`)
  // which on iOS Safari + Android Chrome pops the native share sheet
  // with the PNG pre-attached AND the caption pre-typed. WhatsApp
  // appears as one of the share targets along with IG, X, Telegram —
  // a single tap and the post is composed.
  //
  // Fallback (desktop browsers + older Android WebView): downloads the
  // PNG so the user has it locally, then opens wa.me with the caption
  // pre-typed. The user adds the image manually one extra tap later —
  // not as smooth, but never fails.
  const [sharingPreset, setSharingPreset] = useState(null);
  const handleShareToWhatsApp = async (preset) => {
    if (sharingPreset) return;
    setSharingPreset(preset);
    try {
      const res = await fetch(posterUrl(preset));
      const blob = await res.blob();
      const fileName = `plyx-${vid ? vid + "-" : ""}${preset}.png`;
      const file = new File([blob], fileName, { type: "image/png" });

      // Mobile path — native share sheet with poster + caption.
      if (
        typeof navigator !== "undefined"
        && typeof navigator.canShare === "function"
        && navigator.canShare({ files: [file] })
      ) {
        await navigator.share({
          files: [file],
          title: "Plyx",
          text: caption,
        });
      } else {
        // Desktop / older browsers — save the PNG, then open wa.me.
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(a.href);
        // Tiny delay so the download visibly fires before we open wa.
        setTimeout(() => {
          window.open(
            `https://wa.me/?text=${encodeURIComponent(caption)}`,
            "_blank",
            "noopener,noreferrer",
          );
        }, 250);
      }
    } catch (e) {
      // User-cancelled or unsupported — open wa.me as a soft fallback.
      if (e?.name !== "AbortError") {
        window.open(
          `https://wa.me/?text=${encodeURIComponent(caption)}`,
          "_blank",
          "noopener,noreferrer",
        );
      }
    } finally {
      setSharingPreset(null);
    }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1600);
    } catch {
      /* ignore */
    }
  };

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(caption)}`;
  const twitterHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}`;
  const telegramHref = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(SUGGESTED_CAPTIONS[captionIdx])}`;

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] text-white pb-24"
      data-testid="plyx-promo-page"
    >
      {/* ── Hero / context strip ───────────────────────────────────── */}
      <header className="px-5 pt-10 pb-6 border-b border-white/5">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-amber-300/80">
            <Share2 className="w-3.5 h-3.5" />
            Advert material
          </div>
          <h1 className="mt-3 text-4xl sm:text-5xl font-black leading-[1.05]">
            {vid ? "Promote this video." : "Promote Plyx."}
          </h1>
          <p className="mt-3 text-sm sm:text-base text-white/70 max-w-xl">
            {vid
              ? "Studio-ready posters with a QR pointing straight at this clip — ideal for WhatsApp Status, IG Story, flyers, or anywhere you can drop an image."
              : "Branded posters with a scannable QR pointing at plyx.co.uk. Built for WhatsApp, Instagram Stories, Twitter/X, and old-school flyers."}
          </p>

          <div className="mt-5 flex items-center gap-2 text-xs text-white/60">
            <LinkIcon className="w-3.5 h-3.5" />
            <span
              className="font-mono text-amber-300 truncate"
              data-testid="plyx-promo-share-url"
            >
              {shareUrl}
            </span>
            <button
              type="button"
              onClick={() => copy(shareUrl, "url")}
              className="ml-2 px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 transition text-white/80"
              data-testid="plyx-promo-copy-url"
            >
              {copied === "url" ? (
                <Check className="w-3.5 h-3.5" />
              ) : (
                <Copy className="w-3.5 h-3.5" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* ── Quick share row — get the link out instantly ──────────── */}
      <section className="px-5 pt-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-xs uppercase tracking-[0.18em] text-white/50 mb-3">
            One-tap share
          </div>
          <div className="grid grid-cols-3 gap-3">
            <a
              href={whatsappHref}
              target="_blank"
              rel="noreferrer"
              className="rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white font-semibold py-3 text-center text-sm shadow-lg shadow-emerald-900/30 hover:brightness-110 transition"
              data-testid="plyx-promo-share-whatsapp"
            >
              WhatsApp
            </a>
            <a
              href={twitterHref}
              target="_blank"
              rel="noreferrer"
              className="rounded-2xl bg-black border border-white/15 text-white font-semibold py-3 text-center text-sm hover:bg-white/5 transition"
              data-testid="plyx-promo-share-twitter"
            >
              X / Twitter
            </a>
            <a
              href={telegramHref}
              target="_blank"
              rel="noreferrer"
              className="rounded-2xl bg-sky-500/90 text-white font-semibold py-3 text-center text-sm hover:bg-sky-500 transition"
              data-testid="plyx-promo-share-telegram"
            >
              Telegram
            </a>
          </div>

          {/* Caption picker */}
          <div className="mt-5 rounded-2xl bg-white/[0.04] border border-white/10 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-[0.18em] text-white/50">
                Caption
              </span>
              <div className="flex items-center gap-1">
                {SUGGESTED_CAPTIONS.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setCaptionIdx(i)}
                    className={
                      "h-2 w-6 rounded-full transition " +
                      (i === captionIdx ? "bg-amber-300" : "bg-white/15")
                    }
                    data-testid={`plyx-promo-caption-tab-${i}`}
                    aria-label={`Caption ${i + 1}`}
                  />
                ))}
              </div>
            </div>
            <p
              className="text-sm text-white/85 leading-relaxed"
              data-testid="plyx-promo-caption-text"
            >
              {caption}
            </p>
            <button
              type="button"
              onClick={() => copy(caption, "caption")}
              className="mt-3 inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-full bg-amber-300 text-black hover:bg-amber-200 transition"
              data-testid="plyx-promo-copy-caption"
            >
              {copied === "caption" ? (
                <>
                  <Check className="w-3.5 h-3.5" /> Copied
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" /> Copy caption
                </>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* ── Poster grid ────────────────────────────────────────────── */}
      <section className="px-5 pt-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs uppercase tracking-[0.18em] text-white/50 mb-4">
            Download posters
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {PRESETS.map((p) => (
              <article
                key={p.id}
                className="rounded-3xl overflow-hidden bg-white/[0.03] border border-white/10 hover:border-amber-300/40 transition"
                data-testid={`plyx-promo-card-${p.id}`}
              >
                <div
                  className="bg-black/40 flex items-center justify-center"
                  style={{ aspectRatio: p.aspect }}
                >
                  <img
                    src={posterUrl(p.id)}
                    alt={`${p.label} preview`}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
                <div className="p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{p.label}</div>
                    <div className="text-[11px] text-white/50 font-mono mt-0.5">
                      {p.dim}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleShareToWhatsApp(p.id)}
                      disabled={sharingPreset === p.id}
                      data-testid={`plyx-promo-whatsapp-${p.id}`}
                      title="Send to WhatsApp with poster attached"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-emerald-500 text-white hover:bg-emerald-400 transition disabled:opacity-60"
                    >
                      <Send className="w-3.5 h-3.5" />
                      {sharingPreset === p.id ? "…" : "WhatsApp"}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDownload(p.id)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-amber-300 text-black hover:bg-amber-200 transition"
                      data-testid={`plyx-promo-download-${p.id}`}
                    >
                      <Download className="w-3.5 h-3.5" /> PNG
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Tip strip ──────────────────────────────────────────────── */}
      <section className="px-5 pt-10">
        <div className="max-w-3xl mx-auto rounded-2xl border border-amber-300/20 bg-amber-300/5 p-4 text-sm text-amber-100/90">
          <strong className="text-amber-200">Pro tip · </strong>
          Posting to WhatsApp Status? Use the <em>Square</em> poster — Status
          crops anything taller. For Instagram Story, grab the <em>Story</em>{" "}
          preset; the QR sits safely inside Instagram&apos;s UI overlays.
        </div>
      </section>
    </div>
  );
}
